import type { MetricPoint } from '@/features/quality/types';

/**
 * Lightweight inline-SVG line chart for a metric series (no chart dependency).
 * Points whose timestamp matches a detected anomaly are highlighted in red.
 */
export function MetricChart({
  points,
  anomalyEpochs,
}: {
  points: MetricPoint[];
  anomalyEpochs: Set<number>;
}): JSX.Element {
  if (points.length < 2) {
    return (
      <p className="text-sm text-slate-500">
        Not enough data points yet — profile the dataset a few times to build a series.
      </p>
    );
  }

  const width = 640;
  const height = 180;
  const pad = 24;

  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const x = (i: number): number => pad + (i / (points.length - 1)) * (width - 2 * pad);
  const y = (v: number): number => height - pad - ((v - min) / range) * (height - 2 * pad);

  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label="Metric trend">
      <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} stroke="#e2e8f0" />
      <path d={path} fill="none" stroke="#2453e6" strokeWidth={1.5} />
      {points.map((p, i) => {
        const isAnomaly = anomalyEpochs.has(new Date(p.ts).getTime());
        return (
          <circle
            key={`${p.ts}-${i}`}
            cx={x(i)}
            cy={y(p.value)}
            r={isAnomaly ? 4 : 2}
            fill={isAnomaly ? '#dc2626' : '#2453e6'}
          />
        );
      })}
      <text x={pad} y={14} className="fill-slate-400 text-[10px]">
        max {max}
      </text>
      <text x={pad} y={height - 4} className="fill-slate-400 text-[10px]">
        min {min}
      </text>
    </svg>
  );
}
